import { useEffect, useRef } from "react";

/**
 * Traps Tab inside the returned ref's subtree, closes on Escape, and returns
 * focus to whatever was focused before the overlay opened.
 *
 * A no-op unless `active` is true. ChatShell's drawers pass `active` as
 * `open && isDrawer` — never just `open`. Without that second half, opening
 * "a drawer" that CSS is ignoring (because the viewport is wide enough for
 * the docked layout) would still steal Tab focus into what looks like
 * ordinary, permanently-visible sidebar content. An overlay that is ALWAYS an
 * overlay, like the pricing disclaimer, can pass `open` directly — the extra
 * condition exists for things that are only sometimes modal, not as ceremony.
 *
 * Lifted out of ChatShell.tsx on 2026-08-10 when the disclaimer overlay became
 * the second caller. Moved rather than copied, deliberately: a focus trap is
 * exactly the kind of code that gets duplicated and then fixed once. The
 * behaviour is unchanged from the version the drawers have been using.
 *
 * Moved AGAIN on 2026-08-29, from features/chat to lib, when the upgrade
 * confirmation became the third caller — and a billing dialog importing out of
 * the chat feature would have been the first step toward the copy this file
 * exists to prevent. Same reasoning as last time, one directory further out.
 * Nothing about the behaviour changed with it.
 */
export function useFocusTrap(active: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    restoreFocus.current = document.activeElement as HTMLElement | null;

    function focusable(): HTMLElement[] {
      if (!node) return [];
      return Array.from(
        node.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      );
    }
    focusable()[0]?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      restoreFocus.current?.focus();
    };
  }, [active, onClose]);

  return ref;
}
